/**
 * Shared device-status helpers.
 *
 * The staleness maths itself deliberately stays at each call site: the profile
 * tabs compare `new Date(last_active)` directly, while the devices tables convert
 * from campus-local first (`campusLocalToMs(row.last_active, tzOffset)`). Those are
 * genuinely different, so only the exemption rule is shared.
 */

/**
 * Should the stale check be skipped for this device's manufacturer?
 *
 * Some manufacturers' devices never heartbeat into the system, so `last_active`
 * only ever moves when the device is actually used — or never at all. Running the
 * 30-hour stale test against them flags healthy hardware yellow and tells staff to
 * go test a device that is fine, which trains them to ignore the warning. For those
 * manufacturers "active" means active, full stop.
 *
 * The exempt set is passed in rather than fixed here because each surface shows a
 * different device population, and the sets were specified per surface. If a
 * manufacturer turns out never to heartbeat anywhere, the honest fix is to widen
 * every caller's set rather than to special-case one screen.
 *
 * `manufacture_id` arrives as a number from some procedures and a string from
 * others (see WanderingDevicesTable, which tests `=== 6 || === '6'`), so it is
 * coerced rather than compared strictly.
 *
 * @param {number|string} manufactureId
 * @param {Array<number>} exemptIds — manufacturers exempt on this surface
 * @returns {boolean}
 */
export function skipsStaleCheck(manufactureId, exemptIds) {
  const mid = Number(manufactureId);
  if (!Number.isFinite(mid)) return false;
  return (exemptIds || []).includes(mid);
}
